import { useState, useRef } from 'react'
import { getAccessToken } from '../../services/api'

// Dimensione massima lato lungo in pixel per compressione client-side
const MAX_DIMENSION = 1600
// Qualità JPEG per compressione client-side (0-1)
const COMPRESS_QUALITY = 0.8

/**
 * Comprime un'immagine nel browser usando Canvas API.
 * Ridimensiona a max 1600px e comprime in JPEG quality 0.8.
 * Una foto da 10MB diventa ~200-400KB.
 */
async function compressImage(file: File): Promise<File> {
  // Se il file è già piccolo (< 500KB) non serve comprimere
  if (file.size < 500 * 1024) return file

  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(img.src)

      // Calcola dimensioni proporzionali
      let { width, height } = img
      if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
        if (width > height) {
          height = Math.round((height * MAX_DIMENSION) / width)
          width = MAX_DIMENSION
        } else {
          width = Math.round((width * MAX_DIMENSION) / height)
          height = MAX_DIMENSION
        }
      }

      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        resolve(file) // Fallback: invia originale
        return
      }

      ctx.drawImage(img, 0, 0, width, height)

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            resolve(file)
            return
          }
          // Crea nuovo File con nome originale
          const compressed = new File([blob], file.name, {
            type: 'image/jpeg',
            lastModified: Date.now(),
          })
          resolve(compressed)
        },
        'image/jpeg',
        COMPRESS_QUALITY
      )
    }
    img.onerror = () => reject(new Error('Errore nel caricamento dell\'immagine'))
    img.src = URL.createObjectURL(file)
  })
}

interface ImageUploadProps {
  localeId: number
  currentImage?: string
  galleria?: string[]
  onImageUploaded: (url: string, type: 'principale' | 'galleria') => void
  onGalleryUpdated?: (urls: string[]) => void
}

/**
 * Componente per upload immagini locale
 * Gestisce foto principale e galleria con compressione automatica
 */
export default function ImageUpload({
  localeId,
  currentImage,
  galleria = [],
  onImageUploaded,
  onGalleryUpdated
}: ImageUploadProps) {
  const [uploading, setUploading] = useState(false)
  const [uploadStatus, setUploadStatus] = useState('')
  const [error, setError] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const galleryInputRef = useRef<HTMLInputElement>(null)

  const handleUpload = async (files: FileList | null, type: 'principale' | 'galleria') => {
    if (!files || files.length === 0) return

    setError('')
    setUploading(true)
    setUploadStatus('Compressione immagini...')

    const formData = new FormData()
    formData.append('type', type)

    // Comprimi ogni immagine prima dell'upload
    for (let i = 0; i < files.length; i++) {
      try {
        const compressed = await compressImage(files[i])
        formData.append('images', compressed)
      } catch {
        // Se la compressione fallisce, usa il file originale
        formData.append('images', files[i])
      }
    }

    setUploadStatus('Caricamento in corso...')

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL || 'http://localhost:3000/api'}/admin/locali/${localeId}/upload`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${getAccessToken()}`
          },
          body: formData
        }
      )

      if (!response.ok) {
        const err = await response.json()
        throw new Error(err.error || 'Errore upload')
      }

      const data = await response.json()

      if (type === 'principale' && data.urls[0]) {
        onImageUploaded(data.urls[0], 'principale')
      } else if (type === 'galleria' && onGalleryUpdated) {
        onGalleryUpdated([...galleria, ...data.urls])
      }
    } catch (err: any) {
      setError(err.message || 'Errore durante il caricamento')
    } finally {
      setUploading(false)
      setUploadStatus('')
      // Reset input
      if (fileInputRef.current) fileInputRef.current.value = ''
      if (galleryInputRef.current) galleryInputRef.current.value = ''
    }
  }

  const removeFromGallery = (index: number) => {
    if (onGalleryUpdated) {
      const newGalleria = galleria.filter((_, i) => i !== index)
      onGalleryUpdated(newGalleria)
    }
  }

  // Costruisce URL completo per le immagini
  const getImageUrl = (path: string) => {
    if (path.startsWith('http')) return path
    const baseUrl = import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://localhost:3000'
    return `${baseUrl}${path}`
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Foto principale */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-3">
          Foto Principale
        </label>

        <div className="flex items-start gap-4">
          {/* Preview */}
          <div className="w-48 h-32 bg-gray-800 rounded-lg overflow-hidden flex-shrink-0">
            {currentImage ? (
              <img
                src={getImageUrl(currentImage)}
                alt="Foto principale"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-600">
                <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            )}
          </div>

          {/* Upload button */}
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={(e) => handleUpload(e.target.files, 'principale')}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              {uploading ? uploadStatus || 'Caricamento...' : 'Carica foto'}
            </button>
            <p className="text-xs text-gray-500 mt-2">
              JPG, PNG o WebP. Le foto vengono compresse automaticamente.
            </p>
          </div>
        </div>
      </div>

      {/* Galleria */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-3">
          Galleria ({galleria.length} foto)
        </label>

        {/* Grid galleria */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-4">
          {galleria.map((img, index) => (
            <div key={index} className="relative group aspect-video bg-gray-800 rounded-lg overflow-hidden">
              <img
                src={getImageUrl(img)}
                alt={`Galleria ${index + 1}`}
                className="w-full h-full object-cover"
              />
              <button
                type="button"
                onClick={() => removeFromGallery(index)}
                className="absolute top-2 right-2 p-1.5 bg-red-500/80 hover:bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}

          {/* Add button */}
          <div>
            <input
              ref={galleryInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              className="hidden"
              onChange={(e) => handleUpload(e.target.files, 'galleria')}
            />
            <button
              type="button"
              onClick={() => galleryInputRef.current?.click()}
              disabled={uploading}
              className="w-full aspect-video bg-gray-800 hover:bg-gray-700 border-2 border-dashed border-gray-600 hover:border-gold rounded-lg transition-colors flex flex-col items-center justify-center text-gray-500 hover:text-gold disabled:opacity-50"
            >
              <svg className="w-8 h-8 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span className="text-xs">Aggiungi foto</span>
            </button>
          </div>
        </div>

        <p className="text-xs text-gray-500">
          Puoi caricare più foto contemporaneamente. Max 10 foto. Le immagini vengono compresse automaticamente.
        </p>
      </div>
    </div>
  )
}
